/** Anchor the app in document coordinates; iOS WebViews can mispaint fixed roots. */
export function bindViewport() {
  const root = document.documentElement;
  const previousRestoration = history.scrollRestoration;
  history.scrollRestoration = "manual";
  let viewport: VisualViewport | null = null;
  let frame = 0;
  let browseHeight = window.innerHeight;
  let browseWidth = window.innerWidth;
  let keyboardWasOpen = false;
  let awaitingKeyboardClose = false;
  const timers = new Set<ReturnType<typeof setTimeout>>();

  function update() {
    frame = 0;
    // Embedded browsers may finish creating/replacing their viewport after load.
    if (viewport !== window.visualViewport) {
      viewport?.removeEventListener("resize", schedule);
      viewport?.removeEventListener("scroll", schedule);
      viewport = window.visualViewport;
      viewport?.addEventListener("resize", schedule);
      viewport?.addEventListener("scroll", schedule);
    }
    const active = document.activeElement;
    const editing =
      active instanceof HTMLElement &&
      (active.matches(
        'input:not([type="checkbox"]):not([type="range"]):not([type="file"]):not([type="button"]):not([type="submit"]), textarea',
      ) ||
        active.isContentEditable);
    const unzoomed = !viewport || Math.abs(viewport.scale - 1) < 0.05;
    if (!unzoomed) return; // Keep the unzoomed canvas size while the user pinches/pans.

    const height =
      viewport && Number.isFinite(viewport.height) && viewport.height > 0
        ? viewport.height
        : window.innerHeight;
    // Do not freeze visualViewport.width: during rotation its previous width can
    // make Chrome auto-zoom out and perpetually report an oversized viewport.
    const width = root.clientWidth || window.innerWidth;
    if (Math.abs(width - browseWidth) > 40) {
      awaitingKeyboardClose = false;
      keyboardWasOpen = false;
      browseHeight = window.innerHeight;
      browseWidth = width;
    }
    const keyboard =
      editing &&
      (window.innerHeight - height > 100 ||
        (navigator.maxTouchPoints > 0 && browseHeight - height > 100));
    if (keyboardWasOpen && !editing) awaitingKeyboardClose = true;
    if (awaitingKeyboardClose && height >= browseHeight - 100)
      awaitingKeyboardClose = false;
    // Do not commit the last keyboard-sized viewport after blur. A new orientation
    // or a restored viewport releases this guard, even if no resize event fired.
    const visibleHeight =
      awaitingKeyboardClose && !editing
        ? Math.max(browseHeight, height)
        : height;
    root.toggleAttribute("data-keyboard-open", keyboard);
    root.toggleAttribute(
      "data-compact-viewport",
      width < 960 && visibleHeight <= 700,
    );
    root.toggleAttribute(
      "data-short-viewport",
      width < 960 && visibleHeight <= 580,
    );
    root.style.setProperty("--viewport-height", `${visibleHeight}px`);
    // pageTop includes document scroll and WebView pan. In some iOS versions,
    // offsetTop alone misses part of the initial toolbar/keyboard displacement.
    const pageTop =
      viewport && Number.isFinite(viewport.pageTop)
        ? viewport.pageTop
        : window.scrollY + (viewport?.offsetTop ?? 0);
    const pageLeft =
      viewport && Number.isFinite(viewport.pageLeft)
        ? viewport.pageLeft
        : window.scrollX + (viewport?.offsetLeft ?? 0);
    root.style.setProperty("--viewport-top", `${Math.max(0, pageTop)}px`);
    root.style.setProperty("--viewport-left", `${Math.max(0, pageLeft)}px`);
    if (!editing && !awaitingKeyboardClose) {
      browseHeight = height;
      browseWidth = width;
    }
    keyboardWasOpen = keyboard;
    if (!editing && (window.scrollX || window.scrollY)) window.scrollTo(0, 0);
  }
  function schedule() {
    if (!frame) frame = requestAnimationFrame(update);
  }
  function settle() {
    timers.forEach(clearTimeout);
    timers.clear();
    schedule();
    // Google/iOS browser controls can settle after pageshow without a resize.
    for (const delay of [100, 300, 700, 1500, 3000]) {
      const timer = setTimeout(() => {
        timers.delete(timer);
        schedule();
      }, delay);
      timers.add(timer);
    }
  }
  function orient() {
    awaitingKeyboardClose = false;
    keyboardWasOpen = false;
    browseHeight = window.innerHeight;
    browseWidth = window.innerWidth;
    settle();
  }
  const events = ["resize", "scroll"];
  const settlingEvents = ["pageshow", "load"];
  events.forEach((event) => window.addEventListener(event, schedule));
  settlingEvents.forEach((event) => window.addEventListener(event, settle));
  window.addEventListener("orientationchange", orient);
  document.addEventListener("focusin", settle);
  document.addEventListener("focusout", settle);
  document.addEventListener("visibilitychange", settle);
  update();
  settle();
  return () => {
    cancelAnimationFrame(frame);
    timers.forEach(clearTimeout);
    events.forEach((event) => window.removeEventListener(event, schedule));
    settlingEvents.forEach((event) =>
      window.removeEventListener(event, settle),
    );
    window.removeEventListener("orientationchange", orient);
    viewport?.removeEventListener("resize", schedule);
    viewport?.removeEventListener("scroll", schedule);
    document.removeEventListener("focusin", settle);
    document.removeEventListener("focusout", settle);
    document.removeEventListener("visibilitychange", settle);
    for (const name of ["height", "width", "top", "left"])
      root.style.removeProperty(`--viewport-${name}`);
    root.removeAttribute("data-keyboard-open");
    root.removeAttribute("data-compact-viewport");
    root.removeAttribute("data-short-viewport");
    history.scrollRestoration = previousRestoration;
  };
}
